import { useState } from 'react';
import { Link } from 'react-router';
import { Card, Seccion } from '@/components/Card';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { verificarIntegridad, type ReporteIntegridad } from '@/db/integrity';
import { formatearCentavos } from '@/domain/money';
import { mensajeDeError } from '@/db/tx';

/**
 * Revision de integridad, a peticion de la usuaria.
 *
 * No corre sola al abrir la aplicacion: recorre todas las tablas, y hacerlo en
 * cada arranque costaria tiempo en el telefono sin que casi nunca encuentre algo.
 * Se corre cuando hay una sospecha, o antes de cerrar una semana.
 */
export function VerificarIntegridad() {
  const [reporte, setReporte] = useState<ReporteIntegridad | null>(null);
  const [revisando, setRevisando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function revisar() {
    setRevisando(true);
    setError(null);
    setReporte(null);
    try {
      setReporte(await verificarIntegridad());
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setRevisando(false);
    }
  }

  return (
    <Seccion
      titulo="Revisión de integridad"
      descripcion="Comprueba que los datos no se contradigan entre sí"
    >
      <Card>
        <p className="text-sm text-texto-suave">
          Revisa que las existencias cuadren contra el historial de movimientos y que ningún
          registro apunte a algo que ya no existe.
        </p>
        <div className="mt-3">
          <Button variante="secundario" disabled={revisando} onClick={() => void revisar()}>
            {revisando ? 'Revisando…' : 'Revisar ahora'}
          </Button>
        </div>
      </Card>

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo revisar">
          {error}
        </Banner>
      )}

      {reporte !== null && reporte.sano && (
        <Banner tono="exito" titulo="Todo cuadra">
          Se revisaron {reporte.registrosRevisados} registros y no se encontró ningún problema.
        </Banner>
      )}

      {reporte !== null && !reporte.sano && (
        <>
          <Banner
            tono={reporte.hallazgos.some((h) => h.gravedad === 'alerta') ? 'alerta' : 'aviso'}
            titulo={`${reporte.hallazgos.length} ${
              reporte.hallazgos.length === 1 ? 'hallazgo' : 'hallazgos'
            } en ${reporte.registrosRevisados} registros`}
          >
            Abajo está el detalle de cada uno y qué puedes hacer.
          </Banner>

          <ul className="space-y-2">
            {reporte.hallazgos.map((hallazgo, i) => (
              <li key={`${hallazgo.asunto}-${i}`}>
                <Card
                  className={
                    hallazgo.gravedad === 'alerta' ? 'border-alerta bg-alerta-suave' : ''
                  }
                >
                  <p className="text-sm font-semibold text-texto">
                    {hallazgo.gravedad === 'alerta' ? '⚠ ' : ''}
                    {hallazgo.asunto}
                    <span className="ml-2 font-normal text-texto-suave">
                      ({hallazgo.cuantos})
                    </span>
                  </p>
                  <p className="mt-1 text-sm text-texto-suave">{hallazgo.detalle}</p>
                  <p className="mt-1 text-xs text-texto-tenue">{hallazgo.sugerencia}</p>
                </Card>
              </li>
            ))}
          </ul>

          {reporte.descuadresDeInventario.length > 0 && (
            <>
              <Card>
                <p className="text-sm font-semibold text-texto">Insumos que no cuadran</p>
                <ul className="mt-2 space-y-2 text-sm">
                  {reporte.descuadresDeInventario.map((d) => (
                    <li key={d.nombre} className="border-t border-borde pt-2">
                      <p className="font-medium text-texto">{d.nombre}</p>
                      <p className="text-xs text-texto-suave">
                        Guardado {d.stockGuardado} · según el historial {d.stockDelLibro}
                      </p>
                      <p className="text-xs text-texto-suave">
                        Valor guardado {formatearCentavos(d.valorGuardado)} · según el historial{' '}
                        {formatearCentavos(d.valorDelLibro)}
                      </p>
                    </li>
                  ))}
                </ul>
              </Card>

              <Link to="/inventario">
                <Button variante="primario">Ir a corregir el inventario</Button>
              </Link>
            </>
          )}
        </>
      )}
    </Seccion>
  );
}
